/**
 * Search — step drill-in: the matched / excluded / no-data buckets behind one
 * funnel level, with per-lead provenance chips (which fact matched, from where,
 * verified or not) so a result can be TRUSTED or challenged.
 *
 * Two modes:
 *  - preview: renders the builder's 5-per-bucket samples (fast peek);
 *  - run: pages the full buckets from GET /search-runs/:id/steps/:stepId
 *    (recomputed from the frozen definition against current data).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../../services/api';
import Modal from '../../../ui/Modal';
import { SegmentedControl } from '../filters';
import { LifecycleBadge } from '..';
import type { SearchLeadRow, SearchPreviewStep, SearchStepStats } from '../../types';
import { prettySlug, relTime } from '../../lib';
import { WARNING_LABELS } from '../../pages/search/searchLib';
import { Alert } from '../ui';

type Bucket = 'matched' | 'excluded' | 'no_data';

interface Props {
  open: boolean;
  onClose: () => void;
  stepStats: SearchStepStats | null;
  stepTitle: string;
  mode: 'preview' | 'run';
  /** preview mode: the builder's per-bucket samples. */
  samples?: SearchPreviewStep['samples'];
  /** run mode: the run to page buckets from. */
  runId?: string;
  onOpenLead?: (id: string) => void;
}

const SOURCE_LABELS: Record<string, string> = {
  form: 'Form', ai_extraction: 'AI', recruiter: 'Recruiter', lead: 'Lead form',
};

function FactChips({ row }: { row: SearchLeadRow }) {
  const facts = row.matched_facts || [];
  if (!facts.length) return null;
  return (
    <div className="mt-0.5 flex flex-wrap gap-1">
      {facts.map((f, i) => (
        <span
          key={i}
          title={f.excerpt ? `"${f.excerpt}"` : undefined}
          className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] ${
            f.verified
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'
              : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-300'
          }`}
        >
          <span className="font-medium">{f.label}</span>
          {f.detail && <span className="text-gray-400">{f.detail}</span>}
          <span className="text-gray-400">·</span>
          <span>{SOURCE_LABELS[f.source || ''] || f.source || '—'}{f.confidence ? ` (${f.confidence})` : ''}</span>
          {f.verified && <span>✓</span>}
        </span>
      ))}
    </div>
  );
}

function LeadRow({ row, onOpen }: { row: SearchLeadRow; onOpen?: (id: string) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen ? () => onOpen(row.id) : undefined}
        className="block w-full rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/40"
      >
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800 dark:text-gray-100">
            {row.full_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.phone_e164 || row.email || row.id.slice(0, 8)}
          </span>
          <LifecycleBadge status={row.lifecycle_status} />
          {row.effective_study_slug && (
            <span className="hidden text-xs text-gray-400 sm:inline">{prettySlug(row.effective_study_slug)}</span>
          )}
          {row.created_at && <span className="hidden text-xs text-gray-400 md:inline">{relTime(row.created_at)}</span>}
        </div>
        <FactChips row={row} />
      </button>
    </li>
  );
}

export default function StepDrillIn({ open, onClose, stepStats, stepTitle, mode, samples, runId, onOpenLead }: Props) {
  const [bucket, setBucket] = useState<Bucket>('matched');
  const [rows, setRows] = useState<SearchLeadRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (b: Bucket, offset: number, replace: boolean) => {
    if (mode !== 'run' || !runId || !stepStats) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/crm/search-runs/${runId}/steps/${stepStats.step_id}`, {
        params: { bucket: b, offset, limit: 25 },
      });
      const batch: SearchLeadRow[] = res.data?.rows || [];
      setRows((prev) => (replace ? batch : [...prev, ...batch]));
      setTotal(res.data?.total ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [mode, runId, stepStats]);

  useEffect(() => {
    if (!open) return;
    setBucket('matched');
    setRows([]);
    setTotal(null);
    if (mode === 'run') fetchPage('matched', 0, true);
  }, [open, mode, fetchPage]);

  const switchBucket = (b: Bucket) => {
    setBucket(b);
    if (mode === 'run') {
      setRows([]);
      fetchPage(b, 0, true);
    }
  };

  if (!open || !stepStats) return null;
  const previewRows = mode === 'preview' ? (samples?.[bucket] || []) : rows;
  const bucketCounts: Record<Bucket, number> = {
    matched: stepStats.matched,
    excluded: Math.max(0, stepStats.excluded - stepStats.no_data),
    no_data: stepStats.no_data,
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={`Step drill-in — ${stepTitle}`} maxWidth="max-w-2xl">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            label="Bucket"
            value={bucket}
            onChange={(v) => switchBucket(v as Bucket)}
            options={[
              { value: 'matched', label: `Matched (${bucketCounts.matched.toLocaleString()})` },
              { value: 'excluded', label: `Excluded (${bucketCounts.excluded.toLocaleString()})` },
              { value: 'no_data', label: `No data (${bucketCounts.no_data.toLocaleString()})` },
            ]}
          />
          {stepStats.coverage_pct < 100 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {stepStats.coverage_pct}% of entering leads have any data for this criterion
            </span>
          )}
        </div>

        {stepStats.warnings.length > 0 && (
          <Alert tone="warning" size="sm" icon={false}>
            {stepStats.warnings.map((w) => WARNING_LABELS[w] || w).join(' · ')}
          </Alert>
        )}
        {error && (
          <Alert tone="error" size="sm" icon={false}>{error}</Alert>
        )}

        {bucket === 'no_data' && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            These leads have <span className="font-medium">no data at all</span> for this criterion — they weren't matched OR ruled out.
            If this bucket is large, the criterion may misrepresent the database: consider "No data: kept", or tag more forms so the data exists.
          </p>
        )}

        <ul className="max-h-[50vh] space-y-0.5 overflow-y-auto">
          {previewRows.map((r) => <LeadRow key={r.id} row={r} onOpen={onOpenLead} />)}
          {previewRows.length === 0 && !loading && (
            <li className="px-2 py-4 text-sm text-gray-400">Nobody in this bucket.</li>
          )}
        </ul>

        {loading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />}
        {mode === 'preview' ? (
          <p className="text-xs text-gray-400">
            Showing up to 5 sample leads per bucket — run the search for the full, pageable lists.
          </p>
        ) : (
          total !== null && rows.length < total && !loading && (
            <button
              type="button"
              onClick={() => fetchPage(bucket, rows.length, false)}
              className="w-full rounded-lg border border-gray-300 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Load more ({rows.length.toLocaleString()} of {total.toLocaleString()})
            </button>
          )
        )}
      </div>
    </Modal>
  );
}
